import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';
import * as L from 'leaflet';

import { DeliveryService } from 'src/app/services/delivery.service';
import { StorageService } from 'src/app/shared-services/storage.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { ConfigService } from 'src/app/shared/config';
import { AuthService } from 'src/app/services/auth.service';

// "How to find the machine" — the editor behind the place icon on a route
// stop. New drivers do not know which entrance, which floor, or what the spot
// looks like; this doc (root `systemGuides/{systemId}`) is what tells them,
// and it rides into the driver app inside the shift bundle.
//
// TWO pins, set by clicking the map (OpenStreetMap — no key, no billing):
//   * parking — where the van fits; the driver app's driving destination
//   * entrance — which door; the last stretch on foot
// Pasting "lat, lng" or a Google Maps link still works and feeds whichever
// pin is selected. Drivers can also move both pins from the app, standing on
// the spot — their fix shows up here with attribution.

interface GuidePhoto {
    id: string;
    url: string;
    path?: string;
    caption?: string;
    source?: string;
    addedByName?: string;
    addedAt?: number;
}

type PinKind = 'entrance' | 'parking';

type TextField = 'floor' | 'entranceNote' | 'parkingNote' | 'directions';
const TEXT_FIELDS: TextField[] = ['floor', 'entranceNote', 'parkingNote', 'directions'];
const FIELD_LABELS: Record<TextField, string> = {
    floor: 'Floor', entranceNote: 'Entrance', parkingNote: 'Parking', directions: 'Directions'
};

// One change to the written instructions, as kept on the guide doc (`edits`,
// last 30). Drivers edit live from the app; this is how the office sees it.
interface GuideEdit {
    at: number;
    name?: string;
    source?: 'driver' | 'admin';
    before: Partial<Record<TextField, string>>;
    after: Partial<Record<TextField, string>>;
}

interface EditRow {
    edit: GuideEdit;
    who: string;
    when: string;
    isDriver: boolean;
    changes: { label: string, before: string, after: string }[];
}

export interface GuideDialogData {
    systemId: string;
    systemName: string;
    siteId?: string | null;
}

@Component({
    selector: 'app-guide-dialog',
    templateUrl: './guide-dialog.component.html',
    styleUrls: ['./guide-dialog.component.css']
})
export class GuideDialogComponent implements OnInit, OnDestroy {

    @ViewChild('mapEl') mapEl?: ElementRef<HTMLDivElement>;

    loading = true;
    saving = false;
    uploading = false;

    floor = '';
    entranceNote = '';
    parkingNote = '';
    directions = '';
    pinText = '';
    pinError = '';
    photos: GuidePhoto[] = [];

    /** Change history of the text fields, newest first — a field, not a getter. */
    editRows: EditRow[] = [];
    private edits: GuideEdit[] = [];
    /** The text as loaded, so a save can record exactly what the office changed. */
    private loadedText: Record<TextField, string> = { floor: '', entranceNote: '', parkingNote: '', directions: '' };

    pins: { entrance: { lat: number, lng: number } | null, parking: { lat: number, lng: number } | null }
        = { entrance: null, parking: null };
    selected: PinKind = 'parking';
    /** "moved by <driver> on <date>" — who last placed each pin. */
    pinSetBy: { entrance?: string, parking?: string } = {};

    private geo: { lat: number, lng: number } | null = null;
    private map: L.Map | null = null;
    private markers: { entrance?: L.Marker, parking?: L.Marker } = {};
    private actorEmail = '';

    constructor(
        public dialogRef: MatDialogRef<GuideDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: GuideDialogData,
        private svc: DeliveryService,
        private storage: StorageService,
        private config: ConfigService,
        private auth: AuthService,
        private notifier: NotificationService
    ) {
        this.auth.user$.subscribe(u => { if (u) this.actorEmail = (u as any).email || ''; });
    }

    async ngOnInit() {
        try {
            const g = await this.svc.getGuide(this.data.systemId);
            if (g) {
                this.floor = g.floor || '';
                this.entranceNote = g.entranceNote || '';
                this.parkingNote = g.parkingNote || '';
                this.directions = g.directions || '';
                this.photos = g.photos || [];
                this.edits = Array.isArray(g.edits) ? g.edits : [];
                this.editRows = this.edits.slice().reverse().map(e => this.toEditRow(e));
                const pt = (p: any) => p && typeof p.lat === 'number' && typeof p.lng === 'number'
                    ? { lat: p.lat, lng: p.lng } : null;
                this.pins.entrance = pt(g.entrance);
                this.pins.parking = pt(g.parking);
                this.geo = pt(g.geo);
                this.pinSetBy.entrance = this.setByLabel(g.entranceSetBy);
                this.pinSetBy.parking = this.setByLabel(g.parkingSetBy);
            }
        } catch (err: any) {
            this.notifier.showNotification('error', err?.message || 'Could not load the guide');
        }
        this.loadedText = this.currentText();
        this.loading = false;
        // Set the missing pin by default; the parking pin is the one drivers ask for.
        this.selected = !this.pins.parking ? 'parking' : (!this.pins.entrance ? 'entrance' : 'parking');
        // The map div appears with the next change-detection pass.
        setTimeout(() => this.initMap());
    }

    ngOnDestroy() {
        this.map?.remove();
        this.map = null;
    }

    private setByLabel(s: any): string | undefined {
        if (!s || !s.name) return undefined;
        const when = s.at ? moment.unix(s.at).format('D MMM') : '';
        return `set by ${s.name}${when ? ' · ' + when : ''}`;
    }

    // ---- instruction history -------------------------------------------------

    private currentText(): Record<TextField, string> {
        return {
            floor: this.floor.trim(), entranceNote: this.entranceNote.trim(),
            parkingNote: this.parkingNote.trim(), directions: this.directions.trim()
        };
    }

    private toEditRow(e: GuideEdit): EditRow {
        const isDriver = e.source === 'driver';
        return {
            edit: e,
            isDriver,
            who: (e.name || (isDriver ? 'driver' : 'office')) + (isDriver ? ' (driver)' : ''),
            when: e.at ? moment.unix(e.at).format('D MMM YYYY HH:mm') : '',
            changes: TEXT_FIELDS
                .filter(k => e.after && k in e.after)
                .map(k => ({ label: FIELD_LABELS[k], before: e.before?.[k] || '', after: e.after[k] || '' }))
        };
    }

    // Puts the text back to how it was BEFORE this change, in the form — the
    // operator reviews it and presses Save, which records the revert too.
    revert(row: EditRow) {
        const b = row.edit.before || {};
        if (b.floor !== undefined) this.floor = b.floor;
        if (b.entranceNote !== undefined) this.entranceNote = b.entranceNote;
        if (b.parkingNote !== undefined) this.parkingNote = b.parkingNote;
        if (b.directions !== undefined) this.directions = b.directions;
        this.notifier.showNotification('info', 'Reverted in the form — press Save guide to apply');
    }

    trackByEdit(_: number, r: EditRow) { return r.edit.at + (r.edit.name || ''); }

    // ---- the map -------------------------------------------------------------

    private initMap() {
        if (!this.mapEl || this.map) return;
        this.map = L.map(this.mapEl.nativeElement, { zoomControl: true });
        this.applyBase();

        this.map.on('click', (e: L.LeafletMouseEvent) => {
            this.setPin(this.selected, { lat: e.latlng.lat, lng: e.latlng.lng });
        });

        this.renderMarkers();
        const pts = [this.pins.entrance, this.pins.parking].filter((p): p is { lat: number, lng: number } => !!p);
        if (pts.length >= 2) this.map.fitBounds(L.latLngBounds(pts), { padding: [30, 30], maxZoom: 18 });
        else if (pts.length === 1) this.map.setView(pts[0], 17);
        else if (this.geo) this.map.setView(this.geo, 16);   // geocoded building position
        else this.map.setView([59.91, 10.75], 5);            // no idea — Norway
        setTimeout(() => this.map?.invalidateSize(), 0);     // dialog finishes its layout late
    }

    // Aerial by default, same as the driver app: a loading dock, a yard and a
    // door the van can reach are visible from above and invisible on a street
    // map. The toggle is remembered per browser.
    base: 'sat' | 'map' = (() => {
        try { return localStorage.getItem('mapBase') === 'map' ? 'map' : 'sat'; } catch { return 'sat'; }
    })();
    private baseLayers: L.TileLayer[] = [];

    toggleBase() {
        this.base = this.base === 'sat' ? 'map' : 'sat';
        try { localStorage.setItem('mapBase', this.base); } catch { }
        this.applyBase();
    }

    private applyBase() {
        if (!this.map) return;
        this.baseLayers.forEach(l => l.remove());
        this.baseLayers = this.base === 'sat'
            ? [
                L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
                    maxZoom: 21, maxNativeZoom: 19,
                    attribution: 'Imagery &copy; Esri, Maxar, Earthstar Geographics'
                }),
                L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}', {
                    maxZoom: 21, maxNativeZoom: 19, attribution: ''
                })
            ]
            : [L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19, attribution: '&copy; OpenStreetMap'
            })];
        this.baseLayers.forEach(l => l.addTo(this.map!));
    }

    private pinIcon(kind: PinKind): L.DivIcon {
        return L.divIcon({
            className: '',
            html: `<div class="gm-pin gm-${kind}">${kind === 'parking' ? 'P' : 'IN'}</div>`,
            iconSize: [30, 38],
            iconAnchor: [15, 34]
        });
    }

    private renderMarkers() {
        if (!this.map) return;
        for (const kind of ['entrance', 'parking'] as PinKind[]) {
            this.markers[kind]?.remove();
            this.markers[kind] = undefined;
            const p = this.pins[kind];
            if (!p) continue;
            const m = L.marker(p, { icon: this.pinIcon(kind), draggable: true, title: kind });
            m.on('dragend', () => {
                const ll = m.getLatLng();
                this.pins[kind] = { lat: ll.lat, lng: ll.lng };
                this.pinSetBy[kind] = undefined;
            });
            m.addTo(this.map);
            this.markers[kind] = m;
        }
    }

    setPin(kind: PinKind, p: { lat: number, lng: number }) {
        this.pins[kind] = p;
        this.pinSetBy[kind] = undefined;
        this.renderMarkers();
    }

    clearPin(kind: PinKind) {
        this.pins[kind] = null;
        this.pinSetBy[kind] = undefined;
        this.renderMarkers();
    }

    pinLabel(kind: PinKind): string {
        const p = this.pins[kind];
        return p ? `${p.lat.toFixed(6)}, ${p.lng.toFixed(6)}` : 'not set';
    }

    // Accepts "59.744, 10.204" or any Google Maps URL that carries coordinates
    // (@59.744,10.204 or ?q=59.744,10.204) — feeds the SELECTED pin.
    parsePin() {
        const t = this.pinText.trim();
        this.pinError = '';
        if (!t) return;
        const m = t.match(/(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/)
            || t.match(/@(-?\d{1,2}\.\d+),(-?\d{1,3}\.\d+)/);
        if (!m) {
            this.pinError = 'Paste "lat, lng" or a Google Maps link with coordinates';
            return;
        }
        const lat = parseFloat(m[1]), lng = parseFloat(m[2]);
        if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
            this.pinError = 'Those numbers are not valid coordinates';
            return;
        }
        this.setPin(this.selected, { lat, lng });
        this.map?.setView([lat, lng], 17);
        this.pinText = '';
    }

    // ---- photos --------------------------------------------------------------

    onFilePicked(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        if (!file) return;
        this.uploading = true;
        const id = 'admin_' + Date.now();
        const path = `systemGuides/${this.data.systemId}/${id}.jpg`;
        this.storage.uploadFile(path, file).subscribe({
            next: url => {
                this.photos = [...this.photos, {
                    id, url, path,
                    caption: '',
                    source: 'admin',
                    addedByName: this.actorEmail,
                    addedAt: moment().unix()
                }];
                this.uploading = false;
            },
            error: () => {
                this.uploading = false;
                this.notifier.showNotification('error', 'Upload failed');
            }
        });
    }

    removePhoto(photo: GuidePhoto) {
        if (!confirm('Remove this photo from the guide?')) return;
        this.photos = this.photos.filter(p => p !== photo);
        // Best effort — a missing file must not block cleaning up the guide.
        if (photo.url) {
            try { this.storage.removeFileUsingURL(photo.url).subscribe({ error: () => { } }); }
            catch { }
        }
    }

    photoAttribution(p: GuidePhoto): string {
        const who = p.source === 'driver' ? (p.addedByName || 'driver') : (p.addedByName || 'office');
        const when = p.addedAt ? moment.unix(p.addedAt).format('D MMM YYYY') : '';
        return when ? `${who} · ${when}` : who;
    }

    async save() {
        this.saving = true;
        // Office edits go into the same history as driver edits, so the log
        // reads as one story and a revert is itself revertable.
        const now = this.currentText();
        const before: Partial<Record<TextField, string>> = {}, after: Partial<Record<TextField, string>> = {};
        for (const k of TEXT_FIELDS) {
            if (now[k] !== this.loadedText[k]) { before[k] = this.loadedText[k]; after[k] = now[k]; }
        }
        const edits = Object.keys(after).length
            ? [...this.edits, { at: moment().unix(), name: this.actorEmail, source: 'admin' as const, before, after }].slice(-30)
            : this.edits;
        try {
            await this.svc.saveGuide(this.data.systemId, {
                edits,
                companyId: this.config.getCompanyId(),
                siteId: this.data.siteId || null,
                systemName: this.data.systemName,
                floor: this.floor.trim(),
                entranceNote: this.entranceNote.trim(),
                parkingNote: this.parkingNote.trim(),
                directions: this.directions.trim(),
                entrance: this.pins.entrance,
                parking: this.pins.parking,
                photos: this.photos
            });
            this.notifier.showNotification('success', 'Guide saved');
            this.dialogRef.close(true);
        } catch (err: any) {
            this.notifier.showNotification('error', err?.message || 'Could not save the guide');
        }
        this.saving = false;
    }

    trackByPhoto(_: number, p: GuidePhoto) { return p.id || p.url; }
}
